import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SelectItem } from '@openng/optimus-ui/api';
import { firstValueFrom, forkJoin, interval, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { VIEW_AUDIT } from 'src/app/constants/action-constants';
import {
  FACILITY_DETAIL_CONTAINER,
  MONLOG_DETAILS_CONTAINER,
  FACILITY_SEARCH_CONTAINER,
} from 'src/app/constants/common.constants';
import {
  FACILITY_TAB_PREFIX,
  NEW_FACILITY,
} from 'src/app/constants/location-constants';
import {
  A_AND_E_LOG_CATEGORY,
  A_AND_E_MAINTENANCE,
  M_AND_R_LOG_CATEGORY,
  M_AND_R_MAINTENANCE,
} from 'src/app/constants/monlog-constants';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import {
  dynamicSort,
  getDirtyValues,
} from 'src/app/core/functions/common-functions';
import { BreadCrumb } from 'src/app/model/common/bread-crumb';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { Contact } from 'src/app/model/contacts/contact';
import { ContactList } from 'src/app/model/contacts/contact-list';
import { Facility } from 'src/app/model/locations/facility';
import { ContactBuilderService } from 'src/app/services/builder/contact-builder.service';
import { ContactListApiService } from 'src/app/services/api/contact-list-api.service';
import { ContactListBuilderService } from 'src/app/services/builder/contact-list-builder.service';
import { FacilityBuilderService } from 'src/app/services/builder/facility-builder.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { MonlogsBuilderService } from 'src/app/services/builder/monlogs-builder.service';
import { MonLogsTableService } from 'src/app/services/table/monlogs-table.service';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { MonlogsApiService } from 'src/app/services/api/monlogs-api.service';
import { AreaApiService } from 'src/app/services/api/area-api.service';
import { RegionApiService } from 'src/app/services/api/region-api.service';
import { AreaBuilderService } from 'src/app/services/builder/area-builder.service';
import { RegionBuilderService } from 'src/app/services/builder/region-builder.service';
import moment from 'moment';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
  AutoComplete,
} from '@openng/optimus-ui/autocomplete';
import {
  isNullOrUndefined,
  sortByAdditionalResourceListType,
  sortByName,
  sortByProperty,
} from 'src/app/utils/utils';
import { SapApiService } from 'src/app/services/api/sap-api.service';
import {
  ArchivedEntryStatus,
  FacilityNotepadComponent,
} from '../../components/facility-notepad/facility-notepad.component';
import { ConfigContactListItem } from 'src/app/model/common/config-item';
import { timezones } from 'src/app/constants/timezones';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { FacilityVisitorLogComponent } from '../../components/facility-visitor-log/facility-visitor-log.component';
import {
  DialogService,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import {
  AdditionalResourceList,
  CheckAdditionalResourceList,
} from 'src/app/model/contacts/additional-resource-list';
import { AdditionalResourceListType } from 'src/app/model/contacts/additional-resource-list-type';
import { FacilityContactListEmbedComponent } from '../../components/facility-contact-list-embed/facility-contact-list-embed.component';
import { SelectChangeEvent, Select } from '@openng/optimus-ui/select';
import { MonLogType } from 'src/app/model/admin/mon-log-type';
import { AuditDialogContainerComponent } from 'src/app/core/containers/audit-dialog-container.component';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { InputText } from '@openng/optimus-ui/inputtext';
import { Checkbox } from '@openng/optimus-ui/checkbox';
import { InputMask } from '@openng/optimus-ui/inputmask';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { Textarea } from '@openng/optimus-ui/textarea';
import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@openng/optimus-ui/tabs';
import { Ripple } from '@openng/optimus-ui/ripple';
import { LocationsFormComponent } from '../../components/locations-form/locations-form.component';
import { TranslatePipe } from '@ngx-translate/core';
import { Region } from 'src/app/model/locations/region';
import { Area } from 'src/app/model/locations/area';
import { MultiSelect } from '@openng/optimus-ui/multiselect';
import { BlockableDiv } from 'src/app/common/components/blockable-div/blockable-div.component';
import { FacilityQrCodeComponent } from '../../components/facility-qr-code/facility-qr-code.component';

@Component({
  selector: 'app-facility-details-container',
  templateUrl: './facility-details-container.component.html',
  styleUrls: ['./facility-details-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    BlockUI,
    FormsModule,
    ReactiveFormsModule,
    Select,
    ButtonDirective,
    InputText,
    Checkbox,
    AutoComplete,
    InputMask,
    Tooltip,
    Textarea,
    Tabs,
    TabList,
    Ripple,
    Tab,
    TabPanels,
    TabPanel,
    FacilityContactListEmbedComponent,
    FacilityNotepadComponent,
    LocationsFormComponent,
    TranslatePipe,
    MultiSelect,
    BlockableDiv,
  ],
})
export class FacilityDetailsContainerComponent
  extends DetailsContainer
  implements OnInit, OnDestroy
{
  protected _breadCrumbService = inject(BreadCrumbBuilderService);
  protected _fb = inject(UntypedFormBuilder);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _contactListApi = inject(ContactListApiService);
  protected _contactListBuilder = inject(ContactListBuilderService);
  protected _authApi = inject(AuthApiService);
  protected _contactBuilder = inject(ContactBuilderService);
  protected _facilityBuilder = inject(FacilityBuilderService);
  protected _monLogBuilder = inject(MonlogsBuilderService);
  protected _monLogTableService = inject(MonLogsTableService);
  protected _monLogApi = inject(MonlogsApiService);
  protected _areaApi = inject(AreaApiService);
  protected _regionApi = inject(RegionApiService);
  protected _areaBuilder = inject(AreaBuilderService);
  protected _regionBuilder = inject(RegionBuilderService);
  protected _sapApi = inject(SapApiService);
  protected _dialogService = inject(DialogService);

  readonly embeddedContactLists = viewChild<FacilityContactListEmbedComponent>(
    'embeddedContactLists'
  );

  ref:
    | DynamicDialogRef<FacilityVisitorLogComponent>
    | DynamicDialogRef<FacilityQrCodeComponent>
    | null = null;
  ArchivedEntryStatus = ArchivedEntryStatus;

  timeZones: { label: string; value: string | null }[] = [];
  timezone: string | null = null;
  localTimeInterval?: Subscription;
  DirtyStatus = DirtyStatus;
  facility: Facility | null = null;
  contactList: ContactList | null = null;
  managementContactList: ContactList | null = null;
  contactListsForDropdowns: ConfigContactListItem[] = [];
  breadCrumbSub?: Subscription;
  saving = false;
  addLocationToFacForm?: UntypedFormGroup;
  displayDialog = false;
  unPersistedNewFacilityLocations: any[] = [];
  unPersistedExistingFacilityLocations: any[] = [];
  additionalResourceLists: AdditionalResourceList[] = [];
  additionalResourceListTypes: AdditionalResourceListType[] = [];
  assignedResourceListTypes: string[] = [];

  regions: any[] = [];
  filteredRegions: any[] = [];
  areas: any[] = [];
  filteredAreas: any[] = [];

  contacts: any[] = [];
  filteredContacts: any[] = [];
  facilityBreadCrumb?: BreadCrumb;
  backToSearchResult?: BreadCrumb;
  availableBusinessUnits: SelectItem[] = [];
  monLogTypes: any[] = [];
  canCreate = false;
  canEdit = false;
  canCreateMonLogs = false;
  canAddContactListToFacility = false;
  canRemoveContactListFromFacility = false;
  canSyncSap = false;
  actions: SelectItem[] = [];
  types: SelectItem[] = [];
  selectedAction: any = null;
  isEditting = false;
  locations: any[] = [];
  tabIndex = 0;

  displayLocationsDialog: boolean = false;

  facilityStates: any[] = [];
  facilityCounties: any[] = [];
  facilityTownships: any[] = [];
  locationDialogType = 'State';

  localTime: string | null = null;

  ngOnInit() {
    this.setupTabClosingSubscription();
    this.setupBreadCrumbSub();
    this.addLocationToFacForm = this._fb.group({
      state: [null, Validators.required],
      county: [null, Validators.required],
      township: [null, Validators.required],
      locationId: [null],
    });

    this.initialize();

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.FACILITIES.CREATE_FACILITIES,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.FACILITIES.EDIT_FACILITIES,
    ]);
    this.canCreateMonLogs = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MON_LOGS.CREATE_MON_LOGS,
    ]);
    this.canAddContactListToFacility =
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.LOCATIONS.FACILITIES.ADD_CONTACT_LIST_TO_FACILITY,
      ]);
    this.canRemoveContactListFromFacility =
      this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.LOCATIONS.FACILITIES.REMOVE_CONTACT_LIST_FROM_FACILITY,
      ]);
    this.canSyncSap = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.FACILITIES.SYNC_SAP_FOR_FACILITY,
    ]);
  }

  setupBreadCrumbSub(): void {
    this.breadCrumbSub =
      this._breadCrumbService.breadCrumbSelectedEvent.subscribe(
        (breadCrumb) => {
          this._breadCrumbService.removeBreadCrumb(this.facilityBreadCrumb!);
          const tab = this._tabService.buildNewTab(
            FACILITY_SEARCH_CONTAINER,
            true
          );
          this._tabService.setMobileTab(tab);
        }
      );
  }

  initialize() {
    this.loadContactLists();
    this.loadAllDropDownData();
    this.loadMonLogTypes();
    this.getFacilityFromId();

    this._facilityApi.getFacilityTypes().subscribe((facilityTypes) => {
      const types = [...facilityTypes]
        .sort(dynamicSort('name', 1))
        .map((t) => ({ label: t.name, value: t.id }));
      this.types = [{ label: 'Choose', value: null }, ...types];
    });

    this.availableBusinessUnits = this._authApi
      .getUserBusinessUnits()
      .map((bu) => {
        return { label: bu.name, value: bu.id };
      });

    const refreshInterval = interval(1000);
    this.localTimeInterval = refreshInterval.subscribe(() => {
      if (this.timezone) {
        this.localTime = moment()
          .tz(this.timezone)
          .format('YYYY-MM-DD HH:mm:ss');
      } else {
        this.localTime = 'No Time zone Selected';
      }
    });
  }

  loadAllDropDownData() {
    // load details-container dropdowns
    this.loadDropDownData();

    // load extra dropdowns that aren't needed on other details views
    let observables = [];

    observables.push(
      this._contactApi.getContacts(),
      this._regionApi.getRegions(),
      this._areaApi.getAreas()
    );

    forkJoin(observables)
      .pipe(take(1))
      .subscribe((responses: any[]) => {
        const contactData = [...responses[0]];
        this.contacts = contactData
          .map((c) => this._contactBuilder.buildContact(c))
          .sort(dynamicSort('firstName', 1));

        const regions: Region[] = responses[1];
        this.regions = regions.sort(dynamicSort('name', 1));

        const areas: Area[] = responses[2];
        this.areas = areas.sort(dynamicSort('name', 1));
      });
  }

  getFacilityFromId() {
    const id = this.id;
    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.FACILITIES.CREATE_FACILITIES,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.FACILITIES.EDIT_FACILITIES,
    ]);
    this.canCreateMonLogs = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MON_LOGS.CREATE_MON_LOGS,
    ]);
    this.unPersistedNewFacilityLocations = [];
    this.unPersistedExistingFacilityLocations = [];
    if (id) {
      this._facilityApi.getFacility(id).subscribe({
        next: (facility) => {
          this.facility = facility;
          if (this.facility.timezone) {
            this.timezone = this.facility.timezone;
          }

          if (this.facility.locations && this.facility.locations.length > 0) {
            this.facilityStates = Array.from(
              new Set(this.facility.locations.map((loc) => loc.state))
            );
            this.facilityCounties = Array.from(
              new Map(
                this.facility.locations.map((loc) => [
                  loc.county,
                  {
                    state: loc.state,
                    county: loc.county,
                    __typename: 'County',
                  },
                ])
              ).values()
            );
            this.facilityTownships = this.facility.locations.map((loc) => ({
              id: loc.id,
              state: loc.state,
              county: loc.county,
              township: loc.township,
              __typename: 'Township',
            }));
          }

          this.facility?.AdditionalResourceLists.forEach((resource) => {
            this.additionalResourceLists.push(
              this._contactListBuilder.buildAdditionalResourceList(resource)!
            );
            this.assignedResourceListTypes.push(
              resource.AdditionalResourceListType.name
            );
          });
          this.additionalResourceLists.sort((a, b) =>
            sortByAdditionalResourceListType(a, b)
          );
          this.getAdditionalResourceListTypes();

          this.doCheckComplete = false;
          this.setupScreen();
        },
        error: (error) => {
          console.error(error);
        },
      });
    } else {
      // if there's no id, we need an empty facility as our base
      this.facility = {
        id: '',
        ContactListId: null,
        managementContactListId: null,
        AORs: [],
        company: null,
        companyId: null,
        county: null,
        facilityName: null,
        facilityType: null,
        facilityUid: null,
        locations: [],
        businessUnit: null,
        notes: null,
        state: null,
        township: null,
        address: null,
        gps: null,
        phone: null,
        altPhone: null,
        scadaId: null,
        owner: null,
        ownerId: null,
        area: null,
        areaId: null,
        flocNumber: null,
        timezone: 'America/New_York', // default for new facilities,
        NotepadEntries: [],
        hidden: null,
        autoSignout: true,
        AdditionalResourceLists: [],
        aorsString: '',
      };
      this.timezone = this.facility!.timezone!;
      this.getAdditionalResourceListTypes();
      this.setupScreen();
    }
  }

  async setupScreen() {
    this.findContactList();
    this.buildForm();
    this.loadTimeZones();
    this.updateForm();

    if (this._deviceService.isMobile()) {
      this._translateService
        .get('LOCATION.SCREEN.FACILITY')
        .subscribe((label) => {
          this._breadCrumbService.resetAndAddBreadCrumb(
            new BreadCrumb(label, null, true)
          );
          if (this.facility) {
            this.facilityBreadCrumb = new BreadCrumb(
              this.facility.facilityName!,
              null,
              false
            );
          }
          this._breadCrumbService.addBreadCrumb(this.facilityBreadCrumb!);
        });
    } else {
      const labels = await firstValueFrom(
        forkJoin({
          new: this._translateService.get('LOCATION.LABEL.NEW_FACILITY'),
          regularPrefix: this._translateService.get('LOCATION.SCREEN.FACILITY'),
        })
      );
      if (!this.facility?.id) {
        this._tabService.updateActiveTabLabel(labels.new);
      } else {
        const tab = this._tabService.getTabByIdAndContainer(
          this.facility.id,
          FACILITY_DETAIL_CONTAINER
        );
        this._tabService.updateTabLabel(
          tab!,
          labels.regularPrefix + ': ' + this.facility!.facilityName
        );
      }
    }

    this.updateActionsList();
    this.loading = false;
  }

  updateActionsList() {
    const selectAction$ = this._translateService.get(
      'MONLOG.LABEL.SELECT_ACTION'
    );
    const delete$ = this._translateService.get('COMMON.LABEL.BUTTONS.DELETE');
    const newCall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_CALL'
    );
    const newMRCall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_MR_CALL'
    );
    const newAECall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_AE_CALL'
    );
    const newInternalCall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_CONTROLLER_CALL'
    );
    const viewAudit$ = this._translateService.get(
      'COMMON.LABEL.VIEW_AUDIT_INFO'
    );
    const syncSap$ = this._translateService.get('COMMON.LABEL.SYNC_SAP');
    const viewMusterList$ = this._translateService.get(
      'OPMONITORING.MESSAGES.HEADERS.VIEW_MUSTER_LIST'
    );
    const viewQRCode$ = this._translateService.get(
      'LOCATION.LABEL.VIEW_QR_CODE'
    );

    let select,
      del,
      newCall,
      newMRCall,
      newAECall,
      newInternalCall,
      audit,
      syncSap,
      musterList,
      viewQRCode = null;
    forkJoin([
      selectAction$,
      delete$,
      newCall$,
      newMRCall$,
      newAECall$,
      newInternalCall$,
      viewAudit$,
      syncSap$,
      viewMusterList$,
      viewQRCode$,
    ])
      .pipe(take(1))
      .subscribe((messages) => {
        select = messages[0];
        del = messages[1];
        newCall = messages[2];
        newMRCall = messages[3];
        newAECall = messages[4];
        newInternalCall = messages[5];
        audit = messages[6];
        syncSap = messages[7];
        musterList = messages[8];
        viewQRCode = messages[9];

        this.actions = [{ label: select, value: null }];

        if (this.canEdit) {
          this.actions.push({ label: del, value: 'delete-facility' });
        }

        if (this.canCreateMonLogs) {
          this.actions.push({ label: newCall, value: 'new-call' });
          this.actions.push({ label: newMRCall, value: 'new-mr-call' });
          this.actions.push({ label: newAECall, value: 'new-ae-call' });
          this.actions.push({
            label: newInternalCall,
            value: 'new-internal-call',
          });
        }

        if (this.canSyncSap) {
          this.actions.push({ label: syncSap, value: 'sync-sap' });
        }

        this.actions.push({ label: audit, value: VIEW_AUDIT });
        this.actions.push({ label: musterList, value: 'view-muster-list' });
        this.actions.push({ label: viewQRCode, value: 'view-qr-code' });

        this.actions = [...this.actions];
      });
  }

  actionEvent($event: SelectChangeEvent, object?: any) {
    if ($event.value) {
      this.selectedAction = null;
      if ($event.value === 'new-call') {
        this.createLog('N');
      } else if ($event.value === 'new-mr-call') {
        this.createLog('MR');
      } else if ($event.value === 'new-ae-call') {
        this.createLog('AE');
      } else if ($event.value === 'new-internal-call') {
        this.createLog('I');
      } else if ($event.value === 'delete-facility') {
        if (!this.canEdit) {
          return;
        }
        let confHeader = null,
          ok = null,
          cancel = null,
          message = null;
        const confHeader$ = this._translateService.get(
          'LOCATION.MESSAGES.HEADERS.DELETE_FACILITY'
        );
        const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
        const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
        const message$ = this._translateService.get(
          'LOCATION.MESSAGES.CONFIRMATION.DELETE_FACILITY',
          { name: this.facility!.facilityName }
        );

        forkJoin([confHeader$, ok$, cancel$, message$]).subscribe(
          (messages) => {
            confHeader = messages[0];
            ok = messages[1];
            cancel = messages[2];
            message = messages[3];
          }
        );
        this._confirmationService.confirm({
          header: confHeader!,
          icon: 'fa fa-question-circle',
          acceptVisible: true,
          rejectVisible: true,
          acceptLabel: ok!,
          rejectLabel: cancel!,
          message: message!,
          accept: () => {
            this.saving = true;
            if (this.facility && this.facility.id) {
              // Existing group so delete it
              this._facilityApi.deleteFacility(this.facility.id).subscribe({
                next: (result) => {
                  this.saving = false;
                  this.displayDialog = false;
                },
                error: (error) => {
                  console.error('Error deleting facility', error);
                },
                complete: () => {
                  const index = this._tabService.getActiveIndex();
                  this._tabService.closeTab(index);
                },
              });
            }
          },
          reject: () => {},
        });
      } else if ($event.value === VIEW_AUDIT) {
        if (this.facility && this.facility.id) {
          const history$ = this._facilityApi.getFacilityHistories(
            this.facility.id
          );
          this._dialogService.open(AuditDialogContainerComponent, {
            dismissableMask: true,
            modal: true,
            closable: true,
            width: '25%',
            data: {
              history: history$,
            },
          });
        }
      } else if ($event.value === 'sync-sap') {
        this.syncSapOutagesForFacility();
      } else if ($event.value === 'view-muster-list') {
        this.openMusterListDialog();
      } else if ($event.value === 'view-qr-code') {
        this.openQRCodeDialog();
      }

      if (!isNullOrUndefined(object)) {
        object.clear(null);
      }
    }
  }

  ngOnDestroy(): void {
    this.facility = null;
    this.contactList = null;
    if (this.breadCrumbSub) {
      this.breadCrumbSub.unsubscribe();
    }
    if (this.localTimeInterval) {
      this.localTimeInterval.unsubscribe();
    }
  }

  buildForm() {
    this.form = this._fb.group({
      facilityName: [null, Validators.required],
      facilityUid: [null, Validators.required],
      facilityType: [null, Validators.required],
      address: null,
      gps: null,
      phone: null,
      altPhone: null,
      scadaId: null,
      aors: [[], Validators.required],
      company: [null, Validators.required],
      companyName: [null],
      businessUnit: [null, Validators.required],
      notes: null,
      updater: [{ value: null, disabled: true }],
      locationId: null,
      id: null,
      version: null,
      ContactListId: null,
      managementContactListId: null,
      owner: null,
      area: null,
      region: null,
      flocNumber: null,
      timezone: null,
      hidden: null,
      autoSignout: true,
      AdditionalResourceLists: [],
    });

    this.valueSub = this.form.valueChanges.subscribe((changes) => {
      if (this.form?.dirty) {
        this.changesEvent.emit({
          isDirty: true,
          index: this.index(),
          id: this.id,
        });
      }
    });
  }

  updateForm() {
    if (this.facility) {
      let selectedBusinessUnit = this.facility.businessUnit?.id;
      this.form?.patchValue({
        facilityName: this.facility.facilityName,
        facilityUid: this.facility.facilityUid,
        facilityType: this.facility.FacilityType
          ? this.facility.FacilityType.id
          : null,
        address: this.facility.address,
        aors: this.facility.AORs.map((aor) => aor.id),
        phone: this.facility.phone,
        altPhone: this.facility.altPhone,
        gps: this.facility.gps,
        scadaId: this.facility.scadaId,
        company: this.facility.companyId,
        companyName: this.facility.company,
        businessUnit: selectedBusinessUnit,
        notes: this.facility.notes,
        updater: this.facility.updater,
        id: this.facility.id,
        version: this.facility.version,
        ContactListId: this.facility.ContactListId,
        managementContactListId: this.facility.managementContactListId,
        owner: this.findOwner(),
        area: this.findArea(),
        region: this.findRegion(),
        flocNumber: this.facility.flocNumber,
        timezone: this.timeZones.find(
          (tz) => tz.value === this.facility?.timezone
        ),
        hidden: this.facility.hidden,
        autoSignout: this.facility.autoSignout,
        AdditionalResourceLists: this.embeddedContactLists()?.updateForm(),
      });

      if (!this.facility.id) {
        this.isEditting = true;
      }

      if (!this.canEdit && !this.canCreate) {
        this.form?.disable();
      } else if (this.facility.id && this.isEditting === false) {
        this.form?.disable();
      }
    }
  }

  loadMonLogTypes(): void {
    this._monLogApi.getMonLogTypes().subscribe((types) => {
      this.monLogTypes = types.filter((t: MonLogType) => t.enabled === true);
    });
  }

  loadContactLists() {
    this._contactListApi.getContactListsForDropdown().subscribe({
      next: (contactLists: ConfigContactListItem[]) => {
        if (this.canRemoveContactListFromFacility) {
          this.contactListsForDropdowns = [
            { name: 'None', id: null, additionalResourceListTypeId: null },
          ];
        }

        this.contactListsForDropdowns.push(
          ...contactLists.sort((a, b) => sortByProperty(a, b, 'name'))
        );
      },
      error: (error) => {
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.RETRIEVE_ERROR',
          bodyKey: 'COMMON.MESSAGES.ERROR.RETRIEVE_ERROR_MESSAGE',
        });
      },
    });
  }

  findContactList() {
    this.contactList = null;
    this.managementContactList = null;
    if (!this.facility?.id) {
      // No facility id available so don't look it up
      return;
    }

    // get Contact List for the facility - this checks facility directly, then area, then region & returns the first one it finds
    this._contactListApi
      .getContactListForFacility(this.facility.id)
      .subscribe((list) => {
        if (list?.id === undefined) {
          // no contact list for this facility
          return;
        }
        this.contactList = list;
      });

    this._contactListApi
      .getManagementContactListForFacility(this.facility.id)
      .subscribe((list) => {
        if (list?.id === undefined) {
          // no contact list for this facility
          return;
        }
        this.managementContactList = list;
      });
  }

  saveFacility(fromAction = false) {
    if (
      !this.canCreate &&
      !this.canEdit &&
      !this.canAddContactListToFacility &&
      !this.canRemoveContactListFromFacility
    ) {
      return;
    }
    this.saving = true;
    if (!this.form?.invalid || fromAction === true) {
      const fac = Object.assign({}, this.form?.getRawValue());
      const facility = Object.assign(
        {},
        {
          address: fac.address,
          facilityName: fac.facilityName,
          facilityUid: fac.facilityUid,
          facilityType: fac.facilityType,
          aors: fac.aors,
          company: fac.company,
          companyName: fac.companyName,
          businessUnit: fac.businessUnit,
          notes: fac.notes,
          gps: fac.gps,
          phone: fac.phone,
          altPhone: fac.altPhone,
          scadaId: fac.scadaId,
          id: fac.id,
          ContactListId: fac.ContactListId,
          managementContactListId: fac.managementContactListId,
          version: fac.version,
          deletedAt: null,
          ownerId: fac.owner?.value ? fac.owner?.value : fac.owner,
          areaId: fac.area?.value ? fac.area?.value : fac.area,
          flocNumber: fac?.flocNumber,
          timezone: fac?.timezone,
          hidden: fac.hidden,
          autoSignout: fac.autoSignout,
          AdditionalResourceLists: fac?.AdditionalResourceLists?.map(
            (resource: AdditionalResourceList) => resource.id
          ),
        }
      );

      this.additionalResourceLists = this.additionalResourceLists.sort((a, b) =>
        sortByAdditionalResourceListType(a, b)
      );

      if (!fac.id) {
        this.createFacility(facility);
      } else {
        this.updateFacility();
      }
    } else {
      this.saving = false;
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'LOCATION.MESSAGES.HEADERS.SAVE_FACILITY',
        bodyKey: 'LOCATION.MESSAGES.ERROR.SAVING_FACILITY_ERROR',
      });
    }
  }

  createFacility(facility: any) {
    if (!this.canCreate) {
      return;
    }
    facility.updater = null;
    facility.updatedAt = null;
    facility.createdAt = null;
    let bu = this.availableBusinessUnits.find(
      (abu) => abu.value === facility.businessUnit
    )!;

    const facilityInput: any = Object.assign(
      {},
      {
        id: facility.id,
        ContactListId: facility.ContactListId,
        managementContactListId: facility.managementContactListId,
        address: facility.address ? facility.address : null,
        facilityName: facility.facilityName,
        facilityUid: facility.facilityUid,
        facilityType: facility.facilityType,
        aors: facility.aors,
        company: facility.company,
        gps: facility.gps ? facility.gps : null,
        scadaId: facility.scadaId ? facility.scadaId : null,
        phone: facility.phone ? facility.phone : null,
        altPhone: facility.altPhone ? facility.altPhone : null,
        notes: facility.notes,
        businessUnit: {
          id: bu.value,
          name: bu.label,
        },
        ownerId: facility.ownerId,
        areaId: facility.areaId,
        flocNumber: facility.flocNumber,
        timezone: facility.timezone.value,
        hidden: facility.hidden === null ? false : facility.hidden,
        autoSignout: facility.autoSignout,
        AdditionalResourceLists: facility?.AdditionalResourceLists,
      }
    );

    if (this.unPersistedNewFacilityLocations) {
      this.unPersistedNewFacilityLocations.forEach((i) => {
        i.update.FacilityId = facilityInput.id;
        i.value.FacilityId = facilityInput.id;
      });
      facilityInput['Locations'] = this.unPersistedNewFacilityLocations.map(
        (i) => i.update
      );
    }

    let facName = null;
    if (facilityInput['facilityType']) {
      const array = this.types.filter(
        (t) => t.value === facilityInput['facilityType']
      );
      if (array && array.length > 0) {
        facName = array[0].label;
      }
    }

    this._facilityApi.createFacility(facilityInput).subscribe({
      next: (facility) => {
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
          headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
        });
        this.facility = facility;
        this.saving = false;
        this.findContactList();
        this.updateForm();
        this.form?.markAsPristine();
      },
      error: (error) => {
        this.saving = false;
        this.setEditting(false);
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'LOCATION.MESSAGES.HEADERS.SAVE_FACILITY',
          bodyKey: 'LOCATION.MESSAGES.ERROR.SAVING_FACILITY_ERROR',
        });
      },
      complete: () => {
        if (!this._deviceService.isMobile()) {
          const oldTab = this._tabService.getActiveTab();
          const tab = this._tabService.buildNewTab(
            FACILITY_DETAIL_CONTAINER,
            true,
            undefined,
            this.facility!.id!
          );
          this._tabService.replaceTab(oldTab, tab);
          this.changesEvent.emit({
            isDirty: false,
            index: this.index(),
            id: this.id,
          });
        }
      },
    });
  }

  updateFacility() {
    if (
      !this.canEdit &&
      !this.canAddContactListToFacility &&
      !this.canRemoveContactListFromFacility
    ) {
      return;
    }
    // need to get dirty values from main form.
    const facilityInput = getDirtyValues(this.form);

    facilityInput['locations'] = this.unPersistedExistingFacilityLocations.map(
      (loc) => loc.update
    );

    facilityInput['aors'] = this.form!.controls['aors'].value;

    facilityInput['AdditionalResourceLists'] = this.additionalResourceLists.map(
      (resource) => resource.id
    );

    if (this.form?.controls['company'].touched) {
      facilityInput['company'] = this.form.controls['company'].value;
    }
    let facName = null;
    if (facilityInput['facilityType']) {
      const array = this.types.filter(
        (t) => t.value === facilityInput['facilityType']
      );
      if (array && array.length > 0) {
        facName = array[0].label;
      }
    }

    // If the contactlistid is pristine, that means that we haven't
    // removed it or changed it, and so it won't come in from getDirtyValues
    // but our lambda will remove the list if we don't send it back
    // so we need to re-send it every time we make other changes
    // to the list
    if (this.form?.controls['ContactListId'].pristine) {
      facilityInput['ContactListId'] =
        this.form.controls['ContactListId'].value;
    }

    if (this.form?.controls['managementContactListId'].pristine) {
      facilityInput['managementContactListId'] =
        this.form.controls['managementContactListId'].value;
    }

    if (this.form?.controls['businessUnit'].pristine) {
      facilityInput['businessUnit'] = this.form.controls['businessUnit'].value
        ? this.form.controls['businessUnit'].value
        : [];
    }

    if (this.form?.controls['owner'].pristine) {
      facilityInput['owner'] = this.form.controls['owner'].value;
    }

    if (this.form?.controls['area'].pristine) {
      facilityInput['area'] = this.form.controls['area'].value
        ? this.form.controls['area'].value
        : null;
    }

    if (this.form?.controls['flocNumber'].pristine) {
      facilityInput['flocNumber'] = this.form.controls['flocNumber'].value
        ? this.form.controls['flocNumber'].value
        : null;
    }

    if (this.form?.controls['timezone'].pristine) {
      facilityInput['timezone'] = this.form.controls['timezone'].value.value
        ? this.form.controls['timezone'].value
        : null;
    }

    if (this.facility) {
      const update = this._facilityBuilder.buildUpdateStatement(
        this.facility,
        facilityInput
      );
      const locations = this.unPersistedExistingFacilityLocations;

      update.Locations =
        locations && locations.length > 0 && locations[0]
          ? update.Locations
          : [];

      this._facilityApi.updateFacility(this.facility.id!, update).subscribe({
        next: (facility) => {
          this._logAndMessage.translateToSuccessMessage({
            bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
            headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
          });

          this.facility = facility;
          this.findContactList();
          this.updateForm();
          this.form?.markAsPristine();
          this.saving = false;
          this.setEditting(false);
        },
        error: (error) => {
          console.error(error);
          this.saving = false;
          this.setEditting(false);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'LOCATION.MESSAGES.HEADERS.SAVE_FACILITY',
            bodyKey: 'LOCATION.MESSAGES.ERROR.SAVING_FACILITY_ERROR',
          });
        },
        complete: () => {
          this.changesEvent.emit({
            isDirty: false,
            index: this.index(),
            id: this.id,
          });
        },
      });
    }
  }

  createLog(category: string) {
    const monLog = this._monLogBuilder.buildMonLogFromFacility(
      this.facility!,
      this.contactList!,
      category
    );
    let typeArray: SelectItem[] = [];
    if (category === 'O') {
      typeArray = [
        ...this.monLogTypes.filter(
          (t) => t.name.toLowerCase() === 'emergency locate'
        ),
      ];
    } else if (category === M_AND_R_LOG_CATEGORY) {
      typeArray = [
        ...this.monLogTypes
          .filter(
            (t) => t.name.toLowerCase() === M_AND_R_MAINTENANCE.toLowerCase()
          )
          .map((t) => ({ label: t.name, value: t.id })),
      ];
    } else if (category === A_AND_E_LOG_CATEGORY) {
      typeArray = [
        ...this.monLogTypes
          .filter(
            (t) => t.name.toLowerCase() === A_AND_E_MAINTENANCE.toLowerCase()
          )
          .map((t) => ({ label: t.name, value: t.id })),
      ];
    }

    monLog.MonLogTypes = typeArray as unknown as any;
    this._monLogTableService.setSelected(monLog);
    const tab = this._tabService.buildNewTab(MONLOG_DETAILS_CONTAINER, true);
    this._tabService.openTab(tab, true);
  }

  /**
   * Need to manually remove the ___-___-____ mask from the input mask so
   * the validation works.
   * @param field
   */
  fixMask(field: string) {
    const value: string = this.form?.controls[field].value;
    if (value && value.indexOf('_') > -1) {
      this.form?.controls[field].setValue('');
      this.form?.updateValueAndValidity();
    }
  }

  setEditting(editing: boolean) {
    if (this.canEdit || this.canCreate) {
      this.isEditting = editing;

      if (editing) {
        this.form?.enable();
        this.form?.get('updater')?.disable();
        setTimeout(() => {
          this.embeddedContactLists()?.updateForm();
        }, 1);
      } else {
        this.form?.disable();
      }
    } else if (
      this.canAddContactListToFacility ||
      this.canRemoveContactListFromFacility
    ) {
      this.isEditting = editing;
    } else {
      this.isEditting = false;
      this.form?.disable();
      this._logAndMessage.translateToWarnMessage({
        bodyKey: 'COMMON.MESSAGES.WARN.ADMIN_RIGHTS',
        headerKey: 'COMMON.MESSAGES.HEADERS.AUTHORIZATION_MESSAGE',
      });
    }
  }

  tabChanged($event: any): void {
    if (!isNullOrUndefined($event)) {
      this.tabIndex = $event.index;
    }
  }

  contactSearch($event: AutoCompleteCompleteEvent) {
    if ($event.query) {
      this.filteredContacts = this.contacts
        .filter((c) =>
          (c.firstName + ' ' + c.lastName)
            .toLowerCase()
            .includes($event.query.toLowerCase())
        )
        .filter((c: Contact) => c.inactiveAdAccount === false)
        .map((c) => ({ label: c.firstName + ' ' + c.lastName, value: c.id }));
    } else {
      this.filteredContacts = this.contacts.map((c) => ({
        label: c.firstName + ' ' + c.lastName,
        value: c.id,
      }));
    }
  }

  regionSearch($event: AutoCompleteCompleteEvent) {
    if ($event.query) {
      this.filteredRegions = this.regions
        .filter((r) =>
          r.name.toLowerCase().includes($event.query.toLowerCase())
        )
        .map((r) => ({ label: r.name, value: r.id }));
    } else {
      this.filteredRegions = this.regions.map((r) => ({
        label: r.name,
        value: r.id,
      }));
    }
  }

  selectRegion($event: AutoCompleteSelectEvent) {
    // refilter areas
    this.filteredAreas = this.getFilteredAreas();

    // if the area is already set,
    // ONLY reset it if the area is not in the new filteredAreas
    if (!isNullOrUndefined(this.form?.controls['area'].value)) {
      if (
        !this.filteredAreas.find(
          (a) => a.value === this.form?.controls['area'].value
        )
      ) {
        this.form?.patchValue({
          area: null,
        });
      }
    }
  }

  getFilteredAreas() {
    if (this.form?.controls['region'].value) {
      return this.areas
        .filter((a) => a.regionId === this.form?.controls['region'].value)
        .map((a) => ({ label: a.name, value: a.id }));
    }

    return this.areas.map((a) => ({ label: a.name, value: a.id }));
  }

  areaSearch($event: AutoCompleteCompleteEvent) {
    this.filteredAreas = this.getFilteredAreas();
    if ($event.query) {
      this.filteredAreas = this.filteredAreas
        .filter((a) =>
          a.label.toLowerCase().includes($event.query.toLowerCase())
        )
        .map((a) => ({ label: a.label, value: a.value }));
    }
  }

  areaSelected($event: AutoCompleteSelectEvent) {
    const interior = $event.value;
    if (
      !isNullOrUndefined(interior) &&
      isNullOrUndefined(this.form?.controls['region'].value)
    ) {
      const regionId = this.areas.find((a) => a.id == interior.value).regionId;
      const setRegion = this.regions
        .filter((r) => r.id === regionId)
        .map((r) => ({ label: r.name, value: r.id }))
        .pop();
      this.form?.patchValue({
        region: setRegion,
      });
    }
  }

  findOwner() {
    if (this.facility?.ownerId && this.contacts) {
      return this.contacts
        .filter((c) => c.id == this.facility?.ownerId)
        .map((c) => ({ label: c.firstName + ' ' + c.lastName, value: c.id }))
        .shift();
    }
    return null;
  }

  findArea() {
    if (this.facility?.areaId && this.areas) {
      return this.areas
        .filter((a) => a.id == this.facility?.areaId)
        .map((r) => ({ label: r.name, value: r.id }))
        .shift();
    }
    return null;
  }

  findRegion() {
    if (this.facility?.areaId && this.regions && this.areas) {
      return this.regions
        .filter(
          (r) =>
            r.id ==
            this.areas.find((a) => a.id == this.facility?.areaId).regionId
        )
        .map((r) => ({ label: r.name, value: r.id }))
        .shift();
    }
    return null;
  }

  loadTimeZones() {
    this.timeZones = [...timezones];

    this.timeZones = [{ label: 'Choose', value: null }, ...this.timeZones];
  }

  onTimeZoneChange(
    $event: SelectChangeEvent,
    customForm: UntypedFormGroup = this.form!
  ) {
    const fg = customForm as UntypedFormGroup;
    this.timezone = $event.value.value;
    fg.patchValue({
      timeZone: this.timezone,
    });
  }

  syncSapOutagesForFacility(): void {
    if (!this.facility?.flocNumber) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.HEADERS.NO_FLOC',
        bodyKey: 'COMMON.MESSAGES.ERROR.NO_FLOC_SYNC',
      });
      return;
    }

    this.loading = true;
    this._sapApi.syncSapOutagesForFacility(this.facility.flocNumber).subscribe({
      next: (result) => {
        // As long as we didn't get an exception from the backend, we consider this successful,
        // any errors while creating DB records will be captured in emails sent to the application
        // support group
        this._logAndMessage.translateToSuccessMessage({
          headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
          bodyKey: 'COMMON.MESSAGES.SUCCESS.SUCCESSFUL_SYNC',
        });
        this.loading = false;
      },
      error: (error) => {
        console.error(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.FAILED_SYNC',
          bodyKey: 'COMMON.MESSAGES.ERROR.FAILED_SYNC',
        });
        this.loading = false;
      },
    });
  }

  onNotepadChange() {
    this.initialize();
  }

  onLocationsDialogHidden() {
    this.displayLocationsDialog = false;
  }

  onLocationsDialogUpdated($event: any[]) {
    this.displayLocationsDialog = false;

    if (this.locationDialogType === 'State') {
      this.facilityStates = $event.map((state) => state.label);

      this.facilityCounties = this.facilityCounties.filter((sc) =>
        this.facilityStates.some((state) => state === sc.state)
      );
      this.facilityTownships = this.facilityTownships.filter((st) =>
        this.facilityCounties.some((sc) => sc.county === st.county)
      );
      this.updateLocationInformation();
    } else if (this.locationDialogType === 'County') {
      this.facilityCounties = $event;

      this.facilityTownships = this.facilityTownships.filter((st) =>
        this.facilityCounties.some((sc) => sc.county === st.county)
      );
      this.updateLocationInformation();
    } else {
      this.facilityTownships = $event;
      this.updateLocationInformation();
    }
  }

  updateLocationInformation() {
    if (this.facility && this.facility.id) {
      // Editing Existing Facility
      this.unPersistedExistingFacilityLocations = [];

      this.facilityTownships.forEach((location) => {
        // Added new location
        if (!this.facility?.locations.some((loc) => loc.id === location.id)) {
          const locInput = {
            update: {
              mappedId: '',
              LocationId: location.id,
              FacilityId: this.facility?.id,
              dirtyStatus: DirtyStatus.NEW,
            },
            value: location,
          };

          this.unPersistedExistingFacilityLocations.push(locInput);
        }
      });

      this.facility.locations.forEach((location) => {
        // Removed existing location
        if (!this.facilityTownships.some((loc) => loc.id === location.id)) {
          const locInput = {
            update: {
              mappedId: '',
              LocationId: location.id,
              FacilityId: this.facility?.id,
              dirtyStatus: DirtyStatus.DELETED,
            },
            value: location,
          };

          this.unPersistedExistingFacilityLocations.push(locInput);
        }
      });
    } // Adding New Facility
    else {
      this.unPersistedNewFacilityLocations = [];

      this.facilityTownships.forEach((location) => {
        const fg = this.addLocationToFacForm as UntypedFormGroup;
        fg.patchValue({
          locationId: location.id,
          state: location.state,
          county: { county: location.county, state: location.state },
          township: location,
        });

        fg.controls['county'].markAsDirty();
        fg.controls['state'].markAsDirty();
        fg.controls['township'].markAsDirty();

        const raw = this.addLocationToFacForm?.getRawValue();
        const newLocationFac = this._facilityBuilder.buildNewFacilityLocation(
          this.facility!,
          raw
        );

        this.unPersistedNewFacilityLocations.push(newLocationFac);
        this.facility!.locations = [
          ...this.facility!.locations,
          newLocationFac.value,
        ];

        this.addLocationToFacForm?.reset();
      });
    }
  }

  toggleLocationsDialog(type: string) {
    this.locationDialogType = type;
    this.displayLocationsDialog = true;
    this.setEditting(true);
  }

  getStateListForDisplay() {
    if (this.facilityStates?.length > 0) {
      return this.facilityStates.join(', ');
    } else {
      return '';
    }
  }

  getCountyListForDisplay() {
    if (this.facilityCounties?.length > 0) {
      return this.facilityCounties.map((loc) => loc.county).join(', ');
    } else {
      return '';
    }
  }

  getTownshipListForDisplay() {
    if (this.facilityTownships?.length > 0) {
      return this.facilityTownships.map((loc) => loc.township).join(', ');
    } else {
      return '';
    }
  }

  async openMusterListDialog(): Promise<void> {
    const dialogHeader = await firstValueFrom(
      this._translateService.get(
        'OPMONITORING.MESSAGES.HEADERS.VIEW_MUSTER_LIST'
      )
    );
    this.ref = this._dialogService.open(FacilityVisitorLogComponent, {
      header: dialogHeader,
      width: '50%',
      modal: true,
      dismissableMask: true,
      closable: true,
      data: {
        facilityId: this.facility?.id,
      },
    });
    this.ref?.onClose.subscribe((data) => {
      return;
    });
  }

  getAdditionalResourceListTypes(): void {
    this._contactListApi.getAdditionalResourceTypes().subscribe({
      next: (response: AdditionalResourceListType[]) => {
        this.additionalResourceListTypes = response;
        this.additionalResourceListTypes.sort(sortByName);

        if (this.additionalResourceLists.length > 0) {
          this.getUnassignedResourceListTypes();
        }

        this.embeddedContactLists()?.updateForm();
      },
      error: (error) => {
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'CONTACT.MESSAGES.ERROR.LOAD_ADDITIONAL_RESOURCE_LIST_TYPES',
        });
      },
    });
  }

  getUnassignedResourceListTypes(): void {
    this.additionalResourceListTypes = this.additionalResourceListTypes.filter(
      (resourceListType) =>
        !this.assignedResourceListTypes.includes(resourceListType.name)
    );
    this.additionalResourceListTypes =
      this.additionalResourceListTypes.sort(sortByName);
  }

  updateAdditionalResourceList($event: CheckAdditionalResourceList): void {
    const resourceInformation = $event;
    let updatedResource =
      this._contactListBuilder.buildAdditionalResourceList(
        resourceInformation
      )!;

    if (resourceInformation.isExistingResource) {
      let prevResourceListIndex = this.additionalResourceLists.findIndex(
        (prevResource) =>
          prevResource.AdditionalResourceListType.id ===
          updatedResource.AdditionalResourceListType.id
      );

      //Update Contact List Information for the existing object
      this.additionalResourceLists[prevResourceListIndex].id =
        updatedResource.id;
      this.additionalResourceLists[prevResourceListIndex].name =
        updatedResource.name;
      this.additionalResourceLists[prevResourceListIndex].ContactListMembers =
        updatedResource.ContactListMembers;
    } else {
      // Push the new resource onto the array and update the unassigned resources.
      this.additionalResourceLists.push(updatedResource);

      this.assignedResourceListTypes.push(
        updatedResource.AdditionalResourceListType.name
      );
      this.getUnassignedResourceListTypes();
    }

    this.markARLFormDirty();
  }

  removeAdditionalResourceListEvent($event: AdditionalResourceListType): void {
    const resourceType = $event;
    this.additionalResourceLists = this.additionalResourceLists.filter(
      (resource) => resource.AdditionalResourceListType.id != resourceType.id
    );
    this.assignedResourceListTypes = this.assignedResourceListTypes.filter(
      (assignedType) => assignedType != resourceType.name
    );
    this.additionalResourceListTypes.push(resourceType);
    this.getUnassignedResourceListTypes();
    this.markARLFormDirty();
  }

  markARLFormDirty(): void {
    this.embeddedContactLists()?.updateForm();

    this.form?.patchValue({
      AdditionalResourceLists: this.additionalResourceLists,
    });
    this.form?.controls['AdditionalResourceLists'].markAsDirty();

    // Changes Event handles the dirty status of this page, due to the use of the
    // child component we need to make sure we emit an event so the component sees
    // the updated status
    this.changesEvent.emit({
      isDirty: true,
      index: this.index(),
      id: this.id,
    });
  }

  async openQRCodeDialog(): Promise<void> {
    const dialogHeader = await firstValueFrom(
      this._translateService.get('LOCATION.MESSAGES.HEADERS.VIEW_QR_CODE')
    );
    this.ref = this._dialogService.open(FacilityQrCodeComponent, {
      header: dialogHeader,
      width: '25%',
      modal: true,
      dismissableMask: true,
      closable: true,
      data: {
        facilityUid: this.facility?.facilityUid,
      },
    });
    this.ref?.onClose.subscribe((data) => {
      return;
    });
  }
}
